import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';

import useCart from 'shared/hooks/use-cart';
import { removePersistedValue } from 'shared/utils/persisted-values';

import { KioskSeenDocument } from 'src/gql/graphql';
import { getLogger } from 'src/utils/datadog-logger';
import { KIOSK_ID_STORAGE_KEY, KIOSK_NAME_STORAGE_KEY } from 'src/hooks/use-capture-kiosk-id';

const logger = getLogger('use-kiosk-seen');

type UseKioskSeenParams = {
  isKiosk: boolean;
  kioskId: string | null | undefined;
  dispensaryId: string | null | undefined;
};

/**
 * Fire-and-forget liveness ping on page load so admin can spot a claimed kiosk gone silent.
 *
 * Only clears the stored kiosk id on `success === true && stillClaimed === false` -- an
 * explicit, live answer from this mutation's own response. An earlier version cleared ids based
 * on a cached read and wiped valid devices; network errors and ambiguous/`null` responses must
 * stay a no-op here.
 */
export function useKioskSeen({ isKiosk, kioskId, dispensaryId }: UseKioskSeenParams): void {
  const [kioskSeen] = useMutation(KioskSeenDocument);
  const Cart = useCart();
  // Guards against re-firing on every unrelated re-render of the host layout -- only re-fires if
  // the dispensary/kiosk id pair actually changes (e.g. a fresh claim on this same page load).
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isKiosk || !kioskId || !dispensaryId) {
      return;
    }

    const key = `${dispensaryId}:${kioskId}`;
    if (firedForRef.current === key) {
      return;
    }
    firedForRef.current = key;

    kioskSeen({ variables: { dispensaryId, kioskId } })
      .then(({ data }) => {
        const result = data?.kioskSeen;

        if (result?.success === true && result.stillClaimed === false) {
          removePersistedValue(KIOSK_ID_STORAGE_KEY);
          removePersistedValue(KIOSK_NAME_STORAGE_KEY);
          Cart?.setOriginKioskId?.(null);
        }
      })
      .catch((error: unknown) => {
        logger.warn('kiosk_seen_failed', {
          dispensaryId,
          kioskId,
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }, [isKiosk, kioskId, dispensaryId, kioskSeen, Cart]);
}
